import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  CopyIcon,
  DownloadIcon,
  FileTextIcon,
  ImageIcon,
  PencilIcon,
  SearchIcon,
  Trash2Icon,
  UploadIcon,
  VideoIcon,
  Volume2Icon,
} from 'lucide-react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import {
  useMediaAssets,
  useMediaAssetUsage,
  useRemoveMediaAsset,
  useUpdateMediaAsset,
  useUploadMediaAsset,
} from '@/hooks/use-media-assets'
import type { MediaAsset } from '@/types/media-assets'

export const Route = createFileRoute('/_app/organizacao/midias')({
  component: MediaAssetsPage,
})

const KIND_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'IMAGE', label: 'Imagens' },
  { value: 'VIDEO', label: 'Vídeos' },
  { value: 'AUDIO', label: 'Áudios' },
  { value: 'DOCUMENT', label: 'Documentos' },
  { value: 'FILE', label: 'Arquivos' },
]

const PURPOSE_FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'generic', label: 'Geral' },
  { value: 'knowledge-document', label: 'Conhecimento' },
  { value: 'group-media', label: 'Grupos' },
  { value: 'inbox-media', label: 'Inbox' },
]

const UPLOAD_PURPOSES = PURPOSE_FILTERS.filter(
  (purpose) => purpose.value !== 'all'
)

const KIND_LABELS: Record<string, string> = {
  IMAGE: 'Imagem',
  VIDEO: 'Vídeo',
  AUDIO: 'Áudio',
  DOCUMENT: 'Documento',
  FILE: 'Arquivo',
}

const PURPOSE_LABELS: Record<string, string> = {
  generic: 'Geral',
  'profile-avatar': 'Perfil',
  'org-logo': 'Logo',
  'inbox-media': 'Inbox',
  'knowledge-document': 'Conhecimento',
  'group-media': 'Grupos',
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = value
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function kindIcon(kind: string) {
  if (kind === 'IMAGE') return ImageIcon
  if (kind === 'VIDEO') return VideoIcon
  if (kind === 'AUDIO') return Volume2Icon
  return FileTextIcon
}

function purposeLabel(value: string) {
  return PURPOSE_LABELS[value] ?? value
}

function MediaPreview({ asset }: { asset: MediaAsset }) {
  const Icon = kindIcon(asset.kind)

  if (asset.kind === 'IMAGE') {
    return (
      <img
        src={asset.publicUrl}
        alt={asset.fileName}
        className="h-full w-full object-cover"
        loading="lazy"
      />
    )
  }

  if (asset.kind === 'VIDEO') {
    return (
      <video
        src={asset.publicUrl}
        className="h-full w-full object-cover"
        muted
        preload="metadata"
      />
    )
  }

  if (asset.kind === 'AUDIO') {
    return (
      <div className="bg-muted flex h-full w-full items-center justify-center">
        <Volume2Icon className="text-muted-foreground h-10 w-10" />
      </div>
    )
  }

  return (
    <div className="bg-muted flex h-full w-full items-center justify-center">
      <Icon className="text-muted-foreground h-10 w-10" />
    </div>
  )
}

function MediaCard({
  asset,
  onRename,
  onRemove,
}: {
  asset: MediaAsset
  onRename: (asset: MediaAsset) => void
  onRemove: (asset: MediaAsset) => void
}) {
  const Icon = kindIcon(asset.kind)

  async function copyUrl() {
    await navigator.clipboard.writeText(asset.publicUrl)
    toast.success('URL copiada')
  }

  return (
    <Card className="overflow-hidden rounded-md">
      <div className="bg-muted aspect-[4/3] overflow-hidden">
        <MediaPreview asset={asset} />
      </div>
      <CardContent className="space-y-3 p-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <Icon className="text-muted-foreground h-4 w-4 shrink-0" />
            <p className="truncate text-sm font-medium" title={asset.fileName}>
              {asset.fileName}
            </p>
          </div>
          <p className="text-muted-foreground truncate text-xs">
            {asset.mimeType} · {formatBytes(asset.sizeBytes)}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary">
            {KIND_LABELS[asset.kind] ?? asset.kind}
          </Badge>
          <Badge variant="outline">{purposeLabel(asset.purpose)}</Badge>
          {asset.usageCount > 1 ? (
            <Badge variant="success">{asset.usageCount} usos</Badge>
          ) : null}
        </div>

        <p className="text-muted-foreground text-xs">
          Último uso: {formatDate(asset.lastUsedAt)}
        </p>

        <div className="flex items-center justify-between gap-1">
          <Button
            size="icon"
            variant="ghost"
            title="Copiar URL"
            onClick={copyUrl}
          >
            <CopyIcon className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            title="Renomear"
            onClick={() => onRename(asset)}
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" title="Baixar" asChild>
            <a href={asset.publicUrl} target="_blank" rel="noreferrer">
              <DownloadIcon className="h-4 w-4" />
            </a>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            title="Remover"
            onClick={() => onRemove(asset)}
          >
            <Trash2Icon className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function MediaAssetsPage() {
  const { currentOrgId } = useAuth()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('all')
  const [purpose, setPurpose] = useState('all')
  const [uploadPurpose, setUploadPurpose] = useState('generic')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadName, setUploadName] = useState('')
  const [renameTarget, setRenameTarget] = useState<MediaAsset | null>(null)
  const [renameName, setRenameName] = useState('')
  const [removeTarget, setRemoveTarget] = useState<MediaAsset | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const filters = useMemo(
    () => ({ search, kind, purpose, limit: 60 }),
    [kind, purpose, search]
  )
  const assetsQuery = useMediaAssets(currentOrgId, filters)
  const uploadAsset = useUploadMediaAsset(currentOrgId)
  const updateAsset = useUpdateMediaAsset(currentOrgId)
  const removeAsset = useRemoveMediaAsset(currentOrgId)
  const usageQuery = useMediaAssetUsage(currentOrgId, removeTarget?.id ?? null)

  const assets = assetsQuery.data?.data ?? []
  const total = assetsQuery.data?.total ?? 0
  const isBusy =
    uploadAsset.isPending || updateAsset.isPending || removeAsset.isPending

  function prepareUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setUploadFile(file)
    setUploadName(file.name)
  }

  async function upload() {
    if (!uploadFile) return
    const uploaded = await uploadAsset.mutateAsync({
      file: uploadFile,
      fileName: uploadName.trim() || uploadFile.name,
      purpose: uploadPurpose,
    })
    toast.success(uploaded.reused ? 'Mídia reutilizada' : 'Mídia enviada')
    setUploadFile(null)
    setUploadName('')
  }

  function openRename(asset: MediaAsset) {
    setRenameTarget(asset)
    setRenameName(asset.fileName)
  }

  async function rename() {
    if (!renameTarget) return
    const nextName = renameName.trim()
    if (!nextName || nextName === renameTarget.fileName) {
      setRenameTarget(null)
      return
    }
    await updateAsset.mutateAsync({ id: renameTarget.id, fileName: nextName })
    toast.success('Mídia atualizada')
    setRenameTarget(null)
    setRenameName('')
  }

  async function remove() {
    if (!removeTarget) return
    await removeAsset.mutateAsync({ id: removeTarget.id, force: true })
    toast.success('Mídia removida')
    setRemoveTarget(null)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={ImageIcon}
        title="Mídias"
        description={`${total} itens`}
        actions={
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={prepareUpload}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              <UploadIcon className="h-4 w-4" />
              Enviar
            </Button>
          </>
        }
      />

      <main className="min-h-0 flex-1 space-y-5 overflow-auto p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-60 flex-1">
            <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Buscar mídia..."
              className="pl-9"
            />
          </div>
          <div className="w-52 space-y-1">
            <Label className="text-xs">Categoria</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PURPOSE_FILTERS.map((filter) => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48 space-y-1">
            <Label className="text-xs">Tipo</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_FILTERS.map((filter) => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {assetsQuery.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton key={index} className="h-72 rounded-md" />
            ))}
          </div>
        ) : assets.length ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            {assets.map((asset) => (
              <MediaCard
                key={asset.id}
                asset={asset}
                onRename={openRename}
                onRemove={setRemoveTarget}
              />
            ))}
          </div>
        ) : (
          <div className="border-border bg-card flex min-h-72 flex-col items-center justify-center rounded-md border border-dashed p-8 text-center">
            <ImageIcon className="text-muted-foreground mb-3 h-10 w-10" />
            <h2 className="text-base font-semibold">
              Nenhuma mídia encontrada
            </h2>
          </div>
        )}
      </main>

      <Dialog
        open={!!uploadFile}
        onOpenChange={(open) => {
          if (!open && !uploadAsset.isPending) {
            setUploadFile(null)
            setUploadName('')
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar mídia</DialogTitle>
            <DialogDescription>
              Defina um título interno para encontrar esta mídia com facilidade.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="media-upload-name">Título interno</Label>
              <Input
                id="media-upload-name"
                value={uploadName}
                onChange={(event) => setUploadName(event.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={uploadPurpose} onValueChange={setUploadPurpose}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UPLOAD_PURPOSES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {uploadFile ? (
              <div className="text-muted-foreground rounded-md border p-3 text-sm">
                <p className="text-foreground truncate font-medium">
                  {uploadFile.name}
                </p>
                <p>
                  {uploadFile.type || 'application/octet-stream'} ·{' '}
                  {formatBytes(uploadFile.size)}
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUploadFile(null)}
              disabled={uploadAsset.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void upload()}
              disabled={!uploadName.trim() || uploadAsset.isPending}
            >
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open && !updateAsset.isPending) {
            setRenameTarget(null)
            setRenameName('')
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar título da mídia</DialogTitle>
            <DialogDescription>
              Este título é usado apenas na central para busca e organização. O
              arquivo original não é movido nem recriado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="media-rename-name">Título interno</Label>
            <Input
              id="media-rename-name"
              value={renameName}
              onChange={(event) => setRenameName(event.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameTarget(null)}
              disabled={updateAsset.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void rename()}
              disabled={!renameName.trim() || updateAsset.isPending}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(open) => {
          if (!open && !removeAsset.isPending) setRemoveTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover mídia da central?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  A mídia <strong>{removeTarget?.fileName}</strong> será
                  ocultada da biblioteca. Fluxos e mensagens que já usam esta
                  mídia podem continuar exibindo o arquivo enviado.
                </p>
                {usageQuery.isLoading ? (
                  <p>Verificando onde esta mídia está em uso...</p>
                ) : usageQuery.data?.total ? (
                  <div className="rounded-md border p-3">
                    <p className="text-foreground mb-2 font-medium">
                      Uso rastreado no app:
                    </p>
                    <ul className="space-y-1">
                      {usageQuery.data.items.map((item) => (
                        <li
                          key={item.area}
                          className="flex items-center justify-between gap-3"
                        >
                          <span>{item.label}</span>
                          <Badge variant="secondary">{item.count}</Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p>Nenhum uso ativo foi encontrado nas áreas rastreadas.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeAsset.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void remove()}
              disabled={usageQuery.isLoading || removeAsset.isPending}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
